import { useId } from 'react'

import {
  type BoxProps,
  FieldLabel as EcFieldLabel,
  FieldRoot,
  HStack,
} from '@enechain/ecloud-designsystem'

// chakra v3.29 + React 18 types の既知不具合で `FieldLabelProps` が `{ unstyled }` のみに
// collapse し children を受け取れなくなる。 ecloud component をそのまま使い props を
// 付け直す (`SpotBidding/components/IndividualBidModal.tsx` と同じ回避)。
const FieldLabel = EcFieldLabel as React.FC<
  BoxProps & { htmlFor?: string; id?: string }
>

/** ラベルと中身を結び付けるための id。 用途で使い分ける。 */
export type AutoBidFormRowIds = {
  /** 単一の入力に付ける (`<Input id={ids.controlId} />`)。 */
  controlId: string
  /** ラジオ群など「1 つの control ではない」中身に付ける (`aria-labelledby`)。 */
  labelId: string
}

/**
 * 自動入札ルール modal の**ラベル + 入力**の 1 行 (design `.apr-abform-row`)。
 *
 * ## なぜ `<span>` ではなく DS の Field を使うのか
 *
 * 元は `<div style={{display:'flex'}}><span>ラベル</span><Input aria-label="ラベル" /></div>`
 * で、**ラベルと入力が DOM 上で結び付いていなかった** (同じ文字列を `<span>` と
 * `aria-label` に二重に書いて辻褄を合わせていた)。 見出しとしての `<label>` を
 * 持たせ、 関連付けを 1 箇所に閉じ込める (2026-09-03 デザイナー指摘
 * 「modal 内の各項目は DS の form label 等」)。
 *
 * ## id を呼び出し側へ渡す理由
 *
 * DS `FieldRoot` は配下の **DS field slot** に id を配るが、 素の `Input` /
 * `RadioButton` はその slot ではないので**自動では結び付かない**
 * (実測: `getByLabelText` が "no form control was found associated to that label"
 * で失敗した)。 そこで id をこの component が採番し、 children へ明示的に渡す。
 * 入力が 1 つなら `controlId`、 ラジオ群のように control が複数なら
 * `aria-labelledby={labelId}` を使う。
 *
 * ## ラベル列の幅と入力の位置
 *
 * design `admin/approval-flow.html` の `.apr-abform-label` は
 * `flex: 0 0 124px` の**固定列**。 同値を写す。
 *
 * 入力は**ラベル列の直後** (124px + gap 6px) に置く。 gap は DS `FieldRoot` の
 * 値で、 design の `--space-2` = 8px とは 2px ずれる ([design 差異])。 design 側は
 * `.apr-abform-err` / `.apr-abform-eff-row` が `calc(124px + var(--space-2))` で
 * 字下げしており、 補助表示が入力の左端に揃う前提になっている。
 * DS `FieldRoot` の recipe は `justify-content: space-between` なので、
 * 明示的に `flex-start` へ戻さないと入力がカード右端へ飛ぶ (#7154 の移行時に
 * 混入。 当時の PR 本文は「入力の左端が揃う」 としていたが、 実測では
 * ラベル列の右端から 315〜318px 離れていた)。
 *
 * [design 差異] design の同クラスは `font-weight: 700` だが、 本 component は DS
 * `FieldLabel` の既定 (= canonical role `label` = md/600) に従う。 `.form-label`
 * (components.css:965-971) は 600 で、 700 は本画面のローカル指定。 デザイナー指示
 * 「**DS の** form label」 を優先し、 画面固有の上書きは持ち込まない。
 */
export const AutoBidFormRow = ({
  label,
  children,
}: {
  label: string
  children: (ids: AutoBidFormRowIds) => React.ReactNode
}): React.ReactElement => {
  const controlId = useId()
  const labelId = useId()

  return (
    // [DS差異] `justifyContent` で DS `FieldRoot` の recipe を**意図的に打ち消す**。
    //
    // recipe (`@layer recipes`) が `justify-content: space-between` を持つため、
    // 既定では**入力がカード右端まで飛ぶ** (実測: ラベル列の右端から 315〜318px)。
    // design `.apr-abform-row` は `display:flex; gap:--space-2` で、入力は
    // ラベル列の直後 (124px + 8px) に置く前提 (`.apr-abform-err` /
    // `.apr-abform-eff-row` が `calc(124px + var(--space-2))` で字下げしているのが
    // その証左)。非レイヤーの style prop は recipe に勝つのでここで戻す。
    //
    // DS 側に「ラベル列 + 直後に控え」の横並び recipe が無いための差異で、
    // 同等の recipe が入ったら本上書きは不要になる。
    <FieldRoot justifyContent="flex-start" orientation="horizontal">
      <FieldLabel flex="0 0 124px" margin={0} htmlFor={controlId} id={labelId}>
        {label}
      </FieldLabel>
      {/* 入力 + 単位 + 補助表示を 1 行に並べる (design `.apr-abform-row` は
          gap `--space-2` = 8px)。 */}
      <HStack gap={2}>{children({ controlId, labelId })}</HStack>
    </FieldRoot>
  )
}
